// Undo — one bar at the bottom of the screen for ten seconds after a tap that
// changed a record: "Marked Anna paid by bank · Undo". Tapping Undo sends the
// reverse save under the version the first save came back with, so it lands
// on exactly the row that was changed and nothing newer. Offline, the first
// save is queued (no row back yet): the reverse rides the queue behind it,
// and the queue rewrites the version on a chained save (api.ts chainVersion).

import { mutate, readCache, type Ack } from './api'
import { el, roundConfirmation } from './ui'

export const UNDO_MS = 10_000

let timer: number | null = null

// The last change that can be undone, for Cmd+Z / Ctrl+Z (Liam, 29 Sep: "there's no command Z"). It stays
// available for a couple of minutes after the bar has gone, and only once.
const KEY_UNDO_MS = 120_000
let lastUndo: { run: () => void; at: number } | null = null

// link: an optional second way out of the bar, e.g. "See list" after Save to Recanvass (Liam, 8 Oct 2026), or
// "Message them" after moving an agreed visit (act: opens that person in Messages). Following it leaves the screen,
// which ends the Undo offer like any other navigation.
export type UndoLink = { href: string; label: string } | { act: () => void; label: string }

// The customer the bar names opens their page (Liam, 11 Oct 2026: "the customer name in the undo should be clickable in
// case we want to put more information about them or send a message related to the fact they asked for a delay"),
// underlined in lemon. Found by name in the round's customers as this phone last read them: the longest name the words
// hold whole, and only when one customer has it. None, or a name two customers share, stays plain words; so does the
// customer whose page is already open.
function customerIn(text: string): { id: number; name: string; at: number } | null {
  const list = readCache<{ customers?: { id: number; name: string | null }[] }>('customers')?.data?.customers ?? []
  const letter = /[\p{L}\p{N}]/u
  let best: { id: number; name: string; at: number } | null = null
  let shared = false
  for (const c of list) {
    const name = (c.name ?? '').trim()
    if (name.length < 3 || (best && name.length < best.name.length)) continue
    const at = text.indexOf(name)
    if (at < 0 || letter.test(text[at - 1] ?? '') || letter.test(text[at + name.length] ?? '')) continue
    if (best && best.name === name && best.id !== c.id) { shared = true; continue }
    if (!best || name.length > best.name.length) { best = { id: c.id, name, at }; shared = false }
  }
  if (!best || shared || window.location.hash.startsWith(`#/customer/${best.id}`)) return null
  return best
}
function undoWords(text: string): HTMLElement {
  const span = el('span', { class: 'undo-text' })
  const who = customerIn(text)
  if (!who) { span.append(text); return span }
  span.append(text.slice(0, who.at), el('a', { class: 'undo-who', href: `#/customer/${who.id}` }, who.name), text.slice(who.at + who.name.length))
  return span
}

export function offerUndo(text: string, undo: () => void, link?: UndoLink): void {
  text = roundConfirmation(text)
  document.querySelector('#undo-bar')?.remove()
  if (timer !== null) clearTimeout(timer)
  const bar = el('div', { id: 'undo-bar', role: 'status' })
  // Messages' approved confirmation treatment, on the existing Undo bar so
  // there remains one announcement and one undo action.
  if (document.querySelector('.messages-page, .owed-page')) {
    bar.classList.add('messages-receipt')
    const icon = el('span', { class: 'messages-receipt-icon', 'aria-hidden': 'true' })
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 24 24')
    const check = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    check.setAttribute('d', 'm5 12 4 4L19 6')
    svg.append(check); icon.append(svg); bar.append(icon)
  }

  // The Diary's top line (tidy-up item 4, 20 Oct 2026: it was the layout switch, gone with Original).
  if (document.querySelector('.diary-new .diary-top')) bar.classList.add('diary-receipt')
  if (document.querySelector('#screen.diary-phone-week-view')) {
    bar.classList.add('diary-week-undo-in')
    window.setTimeout(() => bar.classList.remove('diary-week-undo-in'), 220)
  }

  const button = el('button', { type: 'button', class: 'undo-button' }, 'Undo')
  let used = false
  const run = (): void => {
    if (used) return
    used = true
    lastUndo = null
    button.disabled = true
    bar.remove()
    undo()
  }
  lastUndo = { run, at: Date.now() }
  button.addEventListener('click', run)
  bar.append(undoWords(text))
  if (link && 'act' in link) {
    const go = el('button', { type: 'button', class: 'undo-link' }, link.label)
    go.addEventListener('click', () => { bar.remove(); link.act() })
    bar.append(go)
  } else if (link) bar.append(el('a', { class: 'undo-link', href: link.href }, link.label))
  bar.append(button)
  document.body.append(bar)
  timer = setTimeout(() => bar.remove(), UNDO_MS) as unknown as number
}

// Cmd+Z / Ctrl+Z undoes the last change, unless the key is meant for a box being typed in.
document.addEventListener('keydown', (e) => {
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'z') return
  const t = e.target as HTMLElement | null
  if (t && (t.closest('input, textarea, select, [contenteditable]') || document.querySelector('dialog[open]'))) return
  if (!lastUndo || Date.now() - lastUndo.at > KEY_UNDO_MS) return
  e.preventDefault()
  lastUndo.run()
})

export function dismissUndo(): void {
  // Leaving the screen ends the offer for Cmd+Z too: nothing on the next screen undoes a change it cannot see (Codex).
  lastUndo = null
  document.querySelector('#undo-bar')?.remove()
}

export interface UndoPatch {
  text: string            // what the bar says was done
  label: string           // the queue's label for the reverse save
  path: string            // same row as the first save
  ack: Ack                // the first save's answer
  version: number         // the row's version BEFORE the first save
  body: Record<string, unknown> // the fields to put back
  before?: () => void     // optional visual capture immediately before sending the inverse
  onAck?: (ack: Ack) => void // optional acknowledged inverse feedback; never called on conflict
  after?: () => void      // reload the screen once the reverse is acknowledged
  link?: UndoLink // optional "See list" or "Message them" beside Undo
}

// Offer to reverse one PATCH. Nothing is offered when the first save was
// refused (there is nothing to undo); a queued save (status 0) still gets one.
export function undoPatch(p: UndoPatch): void {
  if (!p.ack.ok && p.ack.status !== 0) return
  const row = p.ack.row as { version?: number } | undefined
  const version = typeof row?.version === 'number' ? row.version : p.version + 1
  offerUndo(p.text, () => {
    // A failed decorative capture must never prevent the real inverse.
    try { p.before?.() } catch { /* The inverse still proceeds. */ }
    void mutate({
      label: p.label,
      method: 'PATCH',
      path: p.path,
      body: { version, ...p.body },
      onAck: (ack) => {
        // A refused reverse must be seen, not swallowed — the bar is the
        // owner's safety net and a silent 400 is worse than no bar.
        if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(`Undo was refused: ${ack.error ?? 'rejected'}`)
        try { p.onAck?.(ack) } finally { p.after?.() }
      },
      onConflict: () => p.after?.(),
    })
  }, p.link)
}
